/**
 * Theme validation utilities
 * Ensures theme tokens meet accessibility and design standards
 */

import { Theme, ThemeValidation, ThemeColorScheme } from './types';

/**
 * Calculate contrast ratio between two colors
 * Based on WCAG 2.1 guidelines
 */
function getContrastRatio(color1: string, color2: string): number {
  // Convert hex to RGB
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1]!, 16),
          g: parseInt(result[2]!, 16),
          b: parseInt(result[3]!, 16),
        }
      : null;
  };

  // Calculate relative luminance
  const getLuminance = (r: number, g: number, b: number) => {
    const [rs, gs, bs] = [r, g, b].map(c => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs! + 0.7152 * gs! + 0.0722 * bs!;
  };

  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);

  if (!rgb1 || !rgb2) return 0;

  const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
  const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);

  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);

  return (brightest + 0.05) / (darkest + 0.05);
}

/**
 * Validate color accessibility
 */
function validateColorAccessibility(colors: ThemeColorScheme): {
  errors: string[];
  warnings: string[];
} {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check primary text contrast
  const primaryTextContrast = getContrastRatio(
    colors.text.primary,
    colors.background.primary
  );
  if (primaryTextContrast < 4.5) {
    errors.push(
      `Primary text contrast ratio (${primaryTextContrast.toFixed(2)}) is below WCAG AA standard (4.5:1)`
    );
  }

  // Check secondary text contrast
  const secondaryTextContrast = getContrastRatio(
    colors.text.secondary,
    colors.background.primary
  );
  if (secondaryTextContrast < 3) {
    warnings.push(
      `Secondary text contrast ratio (${secondaryTextContrast.toFixed(2)}) is below recommended minimum (3:1)`
    );
  }

  // Check border visibility
  const borderContrast = getContrastRatio(
    colors.border.primary,
    colors.background.primary
  );
  if (borderContrast < 1.5) {
    warnings.push(
      `Border contrast ratio (${borderContrast.toFixed(2)}) may be too low for visibility`
    );
  }

  return { errors, warnings };
}

/**
 * Validate typography tokens
 */
function validateTypography(typography: Theme['typography']): {
  errors: string[];
  warnings: string[];
} {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check minimum font sizes
  const minFontSize = 12; // Minimum readable font size
  Object.entries(typography.fontSize).forEach(([key, value]) => {
    if (value.size < minFontSize) {
      warnings.push(
        `Font size '${key}' (${value.size}px) is below recommended minimum (${minFontSize}px)`
      );
    }
  });

  // Check line height ratios
  Object.entries(typography.fontSize).forEach(([key, value]) => {
    const lineHeightRatio = value.lineHeight / value.size;
    if (lineHeightRatio < 1.2) {
      warnings.push(
        `Line height ratio for '${key}' (${lineHeightRatio.toFixed(2)}) may be too tight`
      );
    }
    if (lineHeightRatio > 2) {
      warnings.push(
        `Line height ratio for '${key}' (${lineHeightRatio.toFixed(2)}) may be too loose`
      );
    }
  });

  return { errors, warnings };
}

/**
 * Validate spacing tokens
 */
function validateSpacing(spacing: Theme['spacing']): {
  errors: string[];
  warnings: string[];
} {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check touch target sizes
  Object.entries(spacing.touchTargets).forEach(([key, value]) => {
    if (value < 44) {
      errors.push(
        `Touch target '${key}' (${value}px) is below Apple's minimum requirement (44px)`
      );
    }
  });

  // Check spacing scale consistency (should follow 8pt grid)
  Object.entries(spacing.spacing).forEach(([key, value]) => {
    if (key !== '0' && value % 4 !== 0) {
      warnings.push(
        `Spacing '${key}' (${value}px) doesn't follow 4px grid system`
      );
    }
  });

  return { errors, warnings };
}

/**
 * Validate complete theme
 */
export function validateTheme(theme: Theme): ThemeValidation {
  const allErrors: string[] = [];
  const allWarnings: string[] = [];

  // Validate colors
  const colorValidation = validateColorAccessibility(theme.colors);
  allErrors.push(...colorValidation.errors);
  allWarnings.push(...colorValidation.warnings);

  // Validate typography
  const typographyValidation = validateTypography(theme.typography);
  allErrors.push(...typographyValidation.errors);
  allWarnings.push(...typographyValidation.warnings);

  // Validate spacing
  const spacingValidation = validateSpacing(theme.spacing);
  allErrors.push(...spacingValidation.errors);
  allWarnings.push(...spacingValidation.warnings);

  return {
    isValid: allErrors.length === 0,
    errors: allErrors,
    warnings: allWarnings,
  };
}

/**
 * Validate individual color contrast
 */
export function validateColorContrast(
  foreground: string,
  background: string,
  level: 'AA' | 'AAA' = 'AA'
): boolean {
  const ratio = getContrastRatio(foreground, background);
  const requiredRatio = level === 'AAA' ? 7 : 4.5;
  return ratio >= requiredRatio;
}

/**
 * Get contrast ratio between two colors
 */
export { getContrastRatio };
